/**
 * usePushNotifications.js — Phase 1b orchestration
 *
 * Owns the OneSignal Web SDK v16 (User Model) integration:
 *   - SEPARATE-INIT pattern: `app.use(OneSignalVuePlugin)` in main.js binds
 *     only the global $OneSignal namespace; THIS composable runs the real
 *     `OneSignal.init(...)` after `navigator.serviceWorker.ready` so it
 *     reuses our already-registered `/sw.js` (single-SW invariant).
 *   - Lazy/consent: returning grantees init eagerly; non-grantees defer the
 *     init() call until the Enable gesture in the priming dialog.
 *   - Identity: v16 client-side `login(String(user.id))` only, called
 *     UNCONDITIONALLY + idempotently (the SDK no-ops an identical id). The
 *     IV/JWT token endpoint is latent on v16 (contract Section 1, Section 7). Logout drops
 *     the external_id; a shared-device account switch is covered by the
 *     logout→login recipe (pushLogoutCleanup on logout → ensurePushIdentity on
 *     the next login), so there is no separate switch path.
 *
 * MUST be called from `App.vue <script setup>` (deferred-work 2026-06-03):
 * `onScopeDispose` only fires when the composable is invoked inside an
 * active Vue effect scope. Calling it from a plain module (e.g. the
 * PwaAdapter) silently no-ops the teardown.
 *
 * NEVER call from TMA — `PwaAdapter.initPushNotifications()` is the
 * boolean gate. The composable itself bails on isTma() defensively.
 *
 * Dedicated-SW architecture (Section E.1 updated 2026-06-09): OneSignal uses its OWN
 * service worker at /push/onesignal/OneSignalSDKWorker.js with scope
 * /push/onesignal/ — completely separate from the Workbox SW at scope /.
 * See buildInitOptions() below for rationale.
 *
 * Click handler (review Section R4 — F7 popstate trap): `Match:'origin'` +
 * `Action:'focus'` keep the SDK from calling `client.navigate()` which
 * would break Framework7's popstate-driven router. Routing is page-side
 * via `OneSignal.Notifications.addEventListener('click', …)` — wired in
 * Task 3 (deep-link).
 */

import { onScopeDispose, readonly, ref } from 'vue';
import {
  WELCOME_PUSH_BODY,
  WELCOME_PUSH_ICON,
  WELCOME_PUSH_LAST_SENT_KEY,
  WELCOME_PUSH_TITLE,
  WELCOME_PUSH_URL,
} from '@/constants/push.js';
import { isTma } from '@/platform/index.js';
import { useAuthStore } from '@/stores/authStore.js';

const PENDING_PUSH_ROUTE_GLOBAL = '__POOLIT_PENDING_PUSH_ROUTE';
const PENDING_PUSH_ROUTE_SS_KEY = 'poolit:pending-push-route';

// Legacy global snooze — retained ONLY as a defensive fallback when the
// per-user state machine cannot read a userId (e.g. very early boot before
// auth resolves). All new code paths use the per-user priming state below.
const PRIMING_SNOOZE_KEY = 'poolit:push-snooze-until';
const PRIMING_SNOOZE_MS = 7 * 24 * 60 * 60 * 1_000; // 7 days

// Re-export the priming-trigger event channel so callers can import it
// alongside the push API without reaching into the leaf constants file.
// (Emitter components should import from '@/constants/push.js' directly to
// avoid pulling the platform-adapter graph into their dependency tree.)
export { PUSH_PRIMING_TRIGGER_EVENT } from '@/constants/push.js';

// Per-user priming state machine (gap G8). Keyed by user id so re-installs
// or shared-device handoffs never inherit a previous user's "soft-declined"
// flag. Shape: `{ [userId]: { status, lastMoment, lastShownAt } }`.
//   status     'asked' | 'soft-declined' | 'granted' | 'denied'
//   lastMoment 'orderbox' | 'deposit' | 'deadline' | 'profile_status' | ...
//              (free-form, but the App.vue trigger should pass one of the
//              known values). `'profile_status'` is the non-transactional
//              entry point fired from the Profile NotificationsStatusCard so
//              users who never reach a value-moment still see the soft-ask
//              once per snooze window (Sprint Change Proposal 2026-06-05,
//              G6 amend / NEW-G26).
//   lastShownAt   epoch ms — used by the re-ask cadence guard.
const PRIMING_STATE_KEY = 'poolit:push-priming-state';

// Module-scope flags so re-mount or HMR doesn't re-run init().
let _initStarted = false;
let _initResolved = false;
let _onesignalInstance = null; // The namespace returned by useOneSignal().
// Click + foreground + change listeners are wired at-most-once after init resolves.
let _clickListenerBound = false;
let _foregroundListenerBound = false;
let _changeListenerBound = false;

/**
 * Pre-init capability gate (PWA only).
 * Mirrors plan Section 5.1 — gate ALL push UI on this. Returns false in TMA, in
 * non-installed iOS Safari (web push 16.4+ only inside an installed PWA),
 * and in any environment missing SW/PushManager/Notification.
 */
export function canRequestPushPermission() {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') return false;
  if (isTma()) return false;
  if (!('serviceWorker' in navigator) || !('PushManager' in window) || !('Notification' in window)) return false;
  const standalone =
    window.matchMedia?.('(display-mode: standalone)').matches ||
    window.navigator.standalone === true;
  const isApple =
    /iPad|iPhone|iPod/.test(navigator.userAgent) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  if (isApple && !standalone) return false;
  return true;
}

/**
 * `true` if the platform reports a stored permission grant (native API —
 * no SDK needed; safe to call pre-init). Returning grantees bypass the
 * priming dialog and silently `optIn()` after init.
 */
export function nativePermissionGranted() {
  if (typeof window === 'undefined' || !('Notification' in window)) return false;
  try {
    return window.Notification.permission === 'granted';
  } catch {
    return false;
  }
}

/**
 * Snooze plumbing — keyed in localStorage so it survives page reloads.
 * Snoozed state hides the priming dialog for PRIMING_SNOOZE_MS. Failures
 * (storage blocked) default to "not snoozed" so the user still sees the
 * primer next session.
 */
export function isPrimingSnoozed() {
  try {
    const raw = localStorage.getItem(PRIMING_SNOOZE_KEY);
    if (!raw) return false;
    const until = parseInt(raw, 10);
    if (Number.isNaN(until)) return false;
    return Date.now() < until;
  } catch {
    return false;
  }
}

export function snoozePriming() {
  try {
    localStorage.setItem(PRIMING_SNOOZE_KEY, String(Date.now() + PRIMING_SNOOZE_MS));
  } catch {
    /* storage blocked — accept the snooze loss */
  }
}

export function clearPrimingSnooze() {
  try {
    localStorage.removeItem(PRIMING_SNOOZE_KEY);
  } catch { /* noop */ }
}

/**
 * Per-user priming state — gap G8.
 *
 * Replaces the single global snooze with one record per user so that a soft
 * decline by user A on one device never carries over to user B (or to user A
 * after a re-install). The state machine also lets the App.vue trigger
 * re-ask at a DIFFERENT value-moment than the one the user declined,
 * matching research Section 1.4: "Re-surface only at the next genuinely-different
 * high-value moment, never the same one twice."
 *
 * Persisted to localStorage as a JSON map. Read/write failures (private
 * mode, quota) degrade to in-memory defaults so the dialog still works.
 */
function readPrimingStateMap() {
  try {
    const raw = localStorage.getItem(PRIMING_STATE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
}

function writePrimingStateMap(map) {
  try {
    localStorage.setItem(PRIMING_STATE_KEY, JSON.stringify(map));
  } catch { /* storage blocked — accept the loss */ }
}

export function getPrimingState(userId) {
  const fallback = { status: null, lastMoment: null, lastShownAt: 0 };
  if (userId == null) return fallback;
  const map = readPrimingStateMap();
  return { ...fallback, ...(map[String(userId)] || {}) };
}

export function setPrimingState(userId, partial) {
  if (userId == null) return;
  const map = readPrimingStateMap();
  const key = String(userId);
  map[key] = { ...(map[key] || {}), ...partial, lastShownAt: Date.now() };
  writePrimingStateMap(map);
}

export function clearPrimingState(userId) {
  if (userId == null) return;
  const map = readPrimingStateMap();
  delete map[String(userId)];
  writePrimingStateMap(map);
}

/**
 * Build the OneSignal init options (single-source-of-truth).
 *
 * ARCHITECTURE: OneSignal uses a DEDICATED service worker at a subdirectory
 * (/push/onesignal/OneSignalSDKWorker.js, scope /push/onesignal/). This
 * separates it completely from the Workbox PWA service worker at scope /.
 *
 * WHY: Previously, passing serviceWorkerPath:'sw.js' + scope:'/' caused
 * OneSignal to register /sw.js?appId=xxx&sdkVersion=yyy at scope /, which
 * CONFLICTED with the Workbox registration of /sw.js at the same scope.
 * workbox-window's urlsMatch('/sw.js?appId=xxx', '/sw.js') → false, so
 * every OneSignal re-registration was classified "external", needRefresh
 * flipped on every page load, and the update dialog looped infinitely during
 * any rapid-deployment window. Separating scopes eliminates the conflict
 * entirely — OneSignal's SW and the PWA SW never compete.
 *
 * OTHER CONFIG:
 *   - notificationClickHandlerMatch 'origin' + Action 'focus' protect F7
 *     popstate router (deferred-work 2026-06-03).
 *   - requiresUserPrivacyConsent:true → SDK won't subscribe until we call
 *     setConsentGiven(true) on opt-in (EU mode).
 *   - autoRegister:false + no notifyButton → we drive the gesture.
 */
export function buildInitOptions() {
  return {
    appId: import.meta.env.VITE_ONESIGNAL_APP_ID,
    // Dedicated subdirectory SW — no leading slash (OneSignal resolves it
    // relative to the site root). Scope must be at or below the file path.
    serviceWorkerPath: 'push/onesignal/OneSignalSDKWorker.js',
    serviceWorkerParam: { scope: '/push/onesignal/' },
    autoRegister: false,
    autoResubscribe: true,
    requiresUserPrivacyConsent: true,
    notificationClickHandlerMatch: 'origin',
    notificationClickHandlerAction: 'focus',
  };
}

/**
 * Dedup guard for the welcome push.
 *
 * Returns true when the last welcome for this (externalId, subscriptionId)
 * pair was fired within 5 000 ms. Prevents accidental double-fire from the
 * PushSubscription change listener and the firePushOptIn post-success path
 * both resolving within the same tick.
 *
 * Key format: `<externalId>:<subscriptionId>`. A new device or a
 * resubscribe naturally produces a new subscriptionId, so the guard never
 * suppresses legitimate re-fires.
 */
function _shouldSkipWelcome(externalId, subscriptionId) {
  if (!externalId || !subscriptionId) return false;
  const key = `${externalId}:${subscriptionId}`;
  try {
    const raw = localStorage.getItem(WELCOME_PUSH_LAST_SENT_KEY);
    if (!raw) return false;
    const map = JSON.parse(raw);
    if (!map || typeof map !== 'object') return false;
    const sentAt = map[key];
    if (typeof sentAt !== 'number') return false;
    return Date.now() - sentAt < 5_000;
  } catch {
    return false;
  }
}

function _recordWelcomeSent(externalId, subscriptionId) {
  if (!externalId || !subscriptionId) return;
  const key = `${externalId}:${subscriptionId}`;
  try {
    let map = {};
    const raw = localStorage.getItem(WELCOME_PUSH_LAST_SENT_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed === 'object') map = parsed;
    }
    map[key] = Date.now();
    localStorage.setItem(WELCOME_PUSH_LAST_SENT_KEY, JSON.stringify(map));
  } catch { /* storage blocked — accept the loss */ }
}

/**
 * Fire the welcome push via the page-side SW registration.
 *
 * Uses `navigator.serviceWorker.ready` → `registration.showNotification()`
 * directly from the page context (NOT via the SW push listener) so it fires
 * on EVERY new subscription event:
 *   - first-time subscribe
 *   - unsubscribe → resubscribe on the same device
 *   - existing user subscribes a new additional device
 *
 * The 5 000 ms dedup (keyed on externalId + subscriptionId) prevents
 * accidental double-fire when both the PushSubscription change listener and
 * the firePushOptIn post-success path resolve nearly simultaneously.
 *
 * `tag: 'poolit:welcome:<timestamp>'` prevents coalescing with real
 * notification banners while guaranteeing each resubscribe shows its own
 * welcome (unique timestamp per call).
 *
 * `data: { url: WELCOME_PUSH_URL }` is read by the SW's
 * handleNotificationClick (degraded path) as `data.route` — but since we
 * pass `url` here (not `route`), the click opens the URL via `openWindow`.
 * The OneSignal click handler reads `notificationClickHandlerAction:'focus'`
 * which focuses an existing tab on click. For welcome-on-click routing we
 * include `data.route` as well so both paths work.
 *
 * @param {string|null} externalId  - OneSignal external_id (user.id as string)
 * @param {string|null} subscriptionId - OneSignal push subscription ID
 * @returns {Promise<boolean>} - true when the notification was shown
 */
export async function triggerWelcomePush(externalId, subscriptionId) {
  if (isTma()) return false;
  if (!canRequestPushPermission()) return false;
  if (typeof navigator === 'undefined' || !navigator.serviceWorker) return false;
  if (_shouldSkipWelcome(externalId, subscriptionId)) {
    console.info('[Push] welcome dedup — skipping within 5s window');
    return false;
  }
  _recordWelcomeSent(externalId, subscriptionId);
  try {
    const registration = await navigator.serviceWorker.ready;
    await registration.showNotification(WELCOME_PUSH_TITLE, {
      body: WELCOME_PUSH_BODY,
      icon: WELCOME_PUSH_ICON,
      dir: 'rtl',
      tag: `poolit:welcome:${Date.now()}`,
      requireInteraction: false,
      data: {
        // `route` for the SW's handleNotificationClick (degraded path)
        route: WELCOME_PUSH_URL,
        // `url` in case any click handler reads the URL from data.url
        url: WELCOME_PUSH_URL,
      },
    });
    return true;
  } catch (err) {
    console.warn('[Push] triggerWelcomePush showNotification failed:', err);
    return false;
  }
}

/**
 * Resolve the OneSignal page-SDK namespace. The plugin's `useOneSignal()`
 * is a synchronous getter — no setup() context needed. Kept behind a
 * factory so tests can stub it.
 */
async function getOneSignal() {
  if (_onesignalInstance) return _onesignalInstance;
  const mod = await import('@onesignal/onesignal-vue3');
  // Named export wins; some bundlers expose only default — handle both.
  const useOS = mod.useOneSignal || mod.default?.useOneSignal;
  _onesignalInstance = useOS ? useOS() : null;
  return _onesignalInstance;
}

/**
 * Resolve route from a push notification payload. Server-authored per
 * contract Section 9 / Section 10 — never reconstructed from other fields.
 */
function readRouteFromOneSignalEvent(evt) {
  // v16 surfaces additionalData at evt.notification.additionalData;
  // older shapes nest under .data. Try both.
  return (
    evt?.notification?.additionalData?.route ||
    evt?.notification?.data?.route ||
    evt?.additionalData?.route ||
    null
  );
}

/**
 * Origin-only route gate. Same-origin-by-construction (must start with `/`,
 * must NOT contain `://`). Rejects absolute URLs and protocol-relative URLs
 * so a server-side bug or hostile payload can't redirect off-origin via
 * the F7 router.
 */
export function isSafeOriginRoute(route) {
  if (typeof route !== 'string') return false;
  if (!route.startsWith('/')) return false;
  // Reject protocol-relative URLs like "//evil.example/x" — they pass a naive
  // startsWith('/') check but resolve cross-origin (browser uses current
  // scheme). Defense-in-depth against a malformed/hostile data.route payload.
  if (route.startsWith('//')) return false;
  if (route.includes('://')) return false;
  return true;
}

function persistPendingRoute(route) {
  try {
    if (typeof window !== 'undefined') {
      window[PENDING_PUSH_ROUTE_GLOBAL] = route;
    }
  } catch { /* noop */ }
  try {
    sessionStorage.setItem(PENDING_PUSH_ROUTE_SS_KEY, route);
  } catch { /* noop — sessionStorage blocked */ }
}

/**
 * navigateTo — push-driven router entry point.
 *
 * Drives Framework7 v9's main view router with `clearPreviousHistory: false`
 * so the user keeps their back stack (back from a deep-linked totals page
 * should land on the prior page, not the f7 root).
 *
 * When f7 isn't ready yet (warm-but-pre-isReady, or cold-start before
 * App.vue's f7ready callback resolves), the route is persisted to BOTH
 * `window.__POOLIT_PENDING_PUSH_ROUTE` AND `sessionStorage.poolit:pending-
 * push-route`. App.vue drains both on `isReady` → nextTick (covers true
 * cold-start where the page-side listener wasn't bound when the SW posted).
 */
export function navigateTo(route) {
  if (!isSafeOriginRoute(route)) {
    console.warn('[Push] navigateTo rejected non-origin route:', route);
    return false;
  }
  const f7 = typeof window !== 'undefined' ? window.f7Instance : null;
  const router = f7?.views?.main?.router;
  if (f7 && router) {
    try {
      router.navigate(route, { clearPreviousHistory: false });
      return true;
    } catch (err) {
      console.warn('[Push] router.navigate failed, queueing:', err);
      persistPendingRoute(route);
      return false;
    }
  }
  // No router yet — queue for App.vue's isReady drain.
  persistPendingRoute(route);
  return false;
}

/**
 * Wire OneSignal page-side click handler (Task 3 deep-link).
 *
 * `OneSignal.Notifications.addEventListener('click', …)` drives routing when
 * the SDK owns notification display (Phase-1b normal path). We do NOT add a
 * 2nd SW notificationclick listener (`src/sw.js` gates its own listener on
 * `!oneSignalSwLoaded`) — that would double-fire on the same click event.
 *
 * Idempotent: bound at-most-once via `_clickListenerBound`.
 */
async function bindOneSignalClickListener() {
  if (_clickListenerBound) return;
  const OneSignal = await getOneSignal();
  const notifs = OneSignal?.Notifications;
  if (!notifs?.addEventListener) return;
  try {
    notifs.addEventListener('click', (evt) => {
      const route = readRouteFromOneSignalEvent(evt);
      if (!route) {
        console.warn('[Push] click event without data.route — ignoring');
        return;
      }
      // Clear the OS home-screen badge when the user acts on a notification.
      // Feature-detected: Badging API is iOS/iPadOS 16.4+ and Chrome 81+.
      navigator.clearAppBadge?.();
      navigateTo(route);
    });
    _clickListenerBound = true;
  } catch (err) {
    console.warn('[Push] addEventListener(click) failed:', err);
  }
}

/**
 * Wire OneSignal foreground "received" toast (best-effort, Task 3 Section C3).
 *
 * Uses `f7.toast.create` directly — NEVER `f7.notification` (which is a
 * different component family per orchestration Section C3).
 *
 * NOTE (gap G23): `evt.preventDefault()` below is a NO-OP on web push — it
 * only suppresses the OS banner on the mobile NATIVE SDKs (and can throw
 * "Browser does not support preventing display", hence the wrap). On web the
 * OS banner is authoritative and may show alongside our toast on desktop;
 * nothing here gates display/ack on it succeeding.
 *
 * Idempotent: bound at-most-once via `_foregroundListenerBound`.
 */
async function bindOneSignalForegroundListener() {
  if (_foregroundListenerBound) return;
  const OneSignal = await getOneSignal();
  const notifs = OneSignal?.Notifications;
  if (!notifs?.addEventListener) return;
  try {
    notifs.addEventListener('foregroundWillDisplay', (evt) => {
      try {
        const title = evt?.notification?.title || 'Poolit';
        const body = evt?.notification?.body || '';
        const f7 = typeof window !== 'undefined' ? window.f7Instance : null;
        if (f7?.toast?.create) {
          const toast = f7.toast.create({
            text: body ? `${title} — ${body}` : title,
            position: 'top',
            closeTimeout: 4_000,
            cssClass: 'push-foreground-toast',
            on: {
              closed: () => {
                try { toast.destroy(); } catch { /* noop */ }
              },
            },
          });
          toast.open();
        }
        // Web no-op (gap G23): preventDefault only suppresses the OS banner on
        // the native SDKs; harmless + try/caught here. NEVER gate display/ack
        // on this — the OS banner is authoritative on web push.
        if (typeof evt.preventDefault === 'function') evt.preventDefault();
      } catch (err) {
        console.warn('[Push] foregroundWillDisplay handler failed:', err);
      }
    });
    _foregroundListenerBound = true;
  } catch (err) {
    console.warn('[Push] addEventListener(foregroundWillDisplay) failed:', err);
  }
}

/**
 * Wire the PushSubscription 'change' listener (v16 User Model).
 *
 * Fires when:
 *   - A new token is assigned (first subscribe, resubscribe, token refresh)
 *   - optedIn transitions from false → true
 *   - autoResubscribe restores a lapsed subscription on a returning visit
 *
 * We detect "a subscription was just created for this user" by checking
 * `event.current.optedIn === true`. The dedup in triggerWelcomePush (keyed
 * on externalId + subscriptionId) ensures the welcome fires exactly once per
 * new subscription even if autoResubscribe fires the change event on a
 * returning visit where the same subscriptionId is re-populated (the dedup
 * window is only 5 s, so a genuinely new subscription the next day re-fires).
 *
 * Idempotent via `_changeListenerBound`.
 */
function _bindPushSubscriptionChangeListener(OneSignal) {
  if (_changeListenerBound) return;
  try {
    const sub = OneSignal?.User?.PushSubscription;
    if (!sub?.addEventListener) return;
    sub.addEventListener('change', (event) => {
      try {
        const { current, previous } = event || {};
        if (!current?.optedIn) return;
        // Only fire when transitioning to opted-in (not on token-only refreshes
        // where both previous.optedIn and current.optedIn are already true).
        // Exception: when previous.id is absent (fresh install), always fire.
        const isNewSubscription = !previous?.optedIn || !previous?.id;
        if (!isNewSubscription) return;
        const subscriptionId = current.id || null;
        const authStore = useAuthStore();
        const externalId = authStore.user?.id ? String(authStore.user.id) : null;
        triggerWelcomePush(externalId, subscriptionId).catch((err) => {
          console.warn('[Push] triggerWelcomePush (change listener) failed:', err);
        });
      } catch (err) {
        console.warn('[Push] PushSubscription change handler failed:', err);
      }
    });
    _changeListenerBound = true;
  } catch (err) {
    console.warn('[Push] PushSubscription.addEventListener(change) failed:', err);
  }
}

/**
 * Run OneSignal.init() exactly once. Idempotent — repeat calls return the
 * cached promise. The plugin source rejects with "OneSignal is already
 * initialized" on duplicate init; we `.catch()` that rejection so HMR /
 * double-mount don't crash.
 */
let _initPromise = null;
async function ensureInit() {
  if (_initResolved) return true;
  if (_initPromise) return _initPromise;

  if (!canRequestPushPermission()) return false;

  _initStarted = true;
  _initPromise = (async () => {
    try {
      // Wait for OUR sw.js to be ready BEFORE OneSignal init — the SDK
      // reuses an existing matching registration, which is what we want.
      if (navigator.serviceWorker?.ready) {
        try {
          await navigator.serviceWorker.ready;
        } catch (err) {
          console.warn('[Push] serviceWorker.ready rejected:', err);
        }
      }
      const OneSignal = await getOneSignal();
      if (!OneSignal?.init) {
        console.warn('[Push] OneSignal namespace missing init()');
        return false;
      }
      const opts = buildInitOptions();
      if (!opts.appId) {
        console.warn('[Push] VITE_ONESIGNAL_APP_ID missing — skipping init');
        return false;
      }
      try {
        await OneSignal.init(opts);
        _initResolved = true;
        // Bind page-side click + foreground listeners (Task 3 deep-link +
        // foreground toast). Best-effort — failures don't gate init success.
        bindOneSignalClickListener().catch((err) => {
          console.warn('[Push] click listener bind failed:', err);
        });
        bindOneSignalForegroundListener().catch((err) => {
          console.warn('[Push] foreground listener bind failed:', err);
        });
        // Wire PushSubscription.change listener for welcome push trigger.
        // Fires on every new subscription (first-time, resubscribe, new device).
        _bindPushSubscriptionChangeListener(OneSignal);
        return true;
      } catch (err) {
        // The plugin double-init guard rejects with this exact string —
        // treat as success so callers can proceed to login/optIn.
        if (String(err).includes('already initialized')) {
          _initResolved = true;
          bindOneSignalClickListener().catch(() => { /* noop */ });
          bindOneSignalForegroundListener().catch(() => { /* noop */ });
          _bindPushSubscriptionChangeListener(OneSignal);
          return true;
        }
        console.error('[Push] OneSignal.init failed:', err);
        return false;
      }
    } finally {
      _initPromise = null;
    }
  })();

  return _initPromise;
}

/**
 * Identity recipe — log in OneSignal at the SINGLE setSession chokepoint
 * (useAuth.js) and on every boot for an already-authenticated user (G22).
 *
 * Calls `login(String(user.id))` UNCONDITIONALLY: the v16 SDK is idempotent
 * on an identical external_id, so we deliberately DROP the old synchronous
 * `OneSignal.User.externalId === want` short-circuit (gap G21). That getter is
 * not reliably populated right after an awaited SDK call (v16 operation queue),
 * so a stale read could skip a genuinely-needed re-login and let the Telegram
 * rail silently mask a broken web subscription. One cheap idempotent login is
 * the safer trade. Shared-device switch needs no special path — logout fires
 * pushLogoutCleanup() and the next login fires this.
 */
export async function ensurePushIdentity() {
  if (!_initResolved) return false;
  if (isTma()) return false;
  const authStore = useAuthStore();
  // Act-as guard: while impersonating, authStore.user.id is the TARGET member.
  // Binding OneSignal to it would rebind the operator's own device to the
  // target's push identity (external_id collision — the operator would receive
  // the target's pushes and the target's real device could get unsubscribed).
  // Skip the identity bind entirely; the exit-reload runs as the operator again
  // and rebinds correctly. See act-as plan Section 3.3.
  if (authStore.isImpersonating) {
    console.info('[Push] impersonating — skipping ensurePushIdentity (no rebind to target)');
    return false;
  }
  const userId = authStore.user?.id;
  if (!userId) return false;
  const want = String(userId);
  const OneSignal = await getOneSignal();
  if (!OneSignal?.login) return false;
  try {
    await OneSignal.login(want);
    return true;
  } catch (err) {
    console.error('[Push] login() failed:', err);
    return false;
  }
}

/**
 * Opt-out + drop external_id. Called from useAuth.logout() finally{} so
 * the device's push subscription is detached from this user before we
 * clear local session state. PWA-only — useAuth gates !isTelegramEnv().
 */
export async function pushLogoutCleanup() {
  if (!_initResolved) return false;
  if (isTma()) return false;
  const OneSignal = await getOneSignal();
  if (!OneSignal) return false;
  try {
    // optOut() drops the device subscription; logout() drops external_id.
    await OneSignal.User.PushSubscription.optOut();
  } catch (err) {
    console.warn('[Push] optOut() failed:', err);
  }
  try {
    await OneSignal.logout();
  } catch (err) {
    console.warn('[Push] logout() failed:', err);
  }
  return true;
}

/**
 * Initialize the push stack at app boot (PWA-only).
 *
 * Strategy:
 *   - Returning grantee (Notification.permission === 'granted') → init
 *     eagerly so silent optIn + identity sync run without a gesture.
 *   - Default/denied → DO NOT init. The first init() must happen inside
 *     the priming dialog's Enable click (a user gesture) so iOS Safari's
 *     gesture-only permission rule is satisfied.
 *
 * Returns `{ initialized, requiresGesture }` so the caller can decide
 * whether to show the priming dialog.
 */
async function initialize() {
  if (isTma()) return { initialized: false, requiresGesture: false };
  if (!canRequestPushPermission()) return { initialized: false, requiresGesture: false };
  // Act-as guard: don't touch the push stack at all while impersonating — no
  // init, no silent optIn, no identity bind (which ensurePushIdentity already
  // no-ops). The exit-reload runs as the operator and initializes normally.
  // See act-as plan Section 3.3.
  if (useAuthStore().isImpersonating) {
    console.info('[Push] impersonating — skipping push initialize()');
    return { initialized: false, requiresGesture: false };
  }
  if (nativePermissionGranted()) {
    const ok = await ensureInit();
    if (ok) {
      // Honor any prior EU consent automatically for returning grantees.
      try {
        const OneSignal = await getOneSignal();
        await OneSignal?.setConsentGiven(true);
      } catch (err) {
        console.warn('[Push] setConsentGiven failed:', err);
      }
      try {
        const OneSignal = await getOneSignal();
        await OneSignal?.User?.PushSubscription?.optIn();
      } catch (err) {
        console.warn('[Push] silent optIn failed:', err);
      }
      await ensurePushIdentity();
    }
    return { initialized: ok, requiresGesture: false };
  }
  // Permission default / denied — defer init to the Enable gesture.
  return { initialized: false, requiresGesture: true };
}

/**
 * WARM half of the warm/fire split (gap G7).
 *
 * Runs ALL the awaited work that used to live inside the Enable click —
 * dynamic import, `serviceWorker.ready`, `OneSignal.init()`, and the EU
 * consent flip — so that by the time the user taps "Yes" the click handler
 * can call `optIn()` synchronously and keep the iOS gesture-activation
 * token (research Section 1.5: WebKit's transient-activation window is ~1s; a
 * slow await chain ahead of the prompt-triggering call consumes it).
 *
 * Call from the value-moment screen MOUNT — `onMounted` of the order-box
 * checkout-success page, the deposit success screen, etc. It is idempotent
 * (delegates to the cached `_initPromise` after the first call), so a
 * second warm from the same screen is a no-op.
 *
 * Returns true when init resolved and consent is set; false on any failure
 * (TMA, capability gate refused, init failure, SDK missing). Callers that
 * pre-warm and then never fire are fine — there is no state to clean up.
 */
export async function warmPushInit() {
  if (isTma()) return false;
  if (!canRequestPushPermission()) return false;
  const ok = await ensureInit();
  if (!ok) return false;
  try {
    const OneSignal = await getOneSignal();
    await OneSignal?.setConsentGiven(true);
  } catch (err) {
    console.warn('[Push] setConsentGiven failed:', err);
    // Don't fail the warm — optIn() will surface a clearer error if consent
    // is the actual blocker.
  }
  return true;
}

/**
 * FIRE half of the warm/fire split (gap G7).
 *
 * The ONLY awaited work this performs is the SDK's `optIn()` call — every
 * other prerequisite is pre-warmed by `warmPushInit()`. Constructed NOT as
 * an `async` function so the SDK call is invoked synchronously in the
 * gesture handler's tick (an `async` wrapper would push the body into a
 * microtask). The returned promise resolves to `true`/`false` for the
 * caller's UI feedback.
 *
 * Online-guard: navigator.onLine === false → resolve to false without
 * touching the SDK so the primer can re-fire next online session.
 *
 * If a caller forgets to warm first, this falls through to a no-op rather
 * than awaiting init mid-gesture (which would drop the iOS token). It also
 * logs a warning so the missing warm is visible during dev QA.
 */
export function firePushOptIn() {
  if (isTma()) return Promise.resolve(false);
  if (!canRequestPushPermission()) return Promise.resolve(false);
  if (typeof navigator !== 'undefined' && navigator.onLine === false) return Promise.resolve(false);
  if (!_initResolved || !_onesignalInstance) {
    console.warn('[Push] firePushOptIn called before warmPushInit resolved — gesture lost');
    return Promise.resolve(false);
  }
  const sub = _onesignalInstance?.User?.PushSubscription;
  if (!sub?.optIn) return Promise.resolve(false);
  let optInPromise;
  try {
    optInPromise = sub.optIn();
  } catch (err) {
    console.error('[Push] optIn() threw synchronously:', err);
    return Promise.resolve(false);
  }
  return Promise.resolve(optInPromise)
    .then(() => {
      // Identity bind + best-effort verification run AFTER the gesture token
      // is already spent on optIn(). Failures here don't change the return.
      ensurePushIdentity().catch(() => { /* noop */ });
      try {
        const optedIn = _onesignalInstance?.User?.PushSubscription?.optedIn;
        if (optedIn === false) {
          console.warn('[Push] optIn() resolved but subscription is not optedIn yet');
        }
      } catch { /* noop */ }
      return true;
    })
    .catch((err) => {
      console.error('[Push] optIn() failed:', err);
      return false;
    });
}

/**
 * Synchronous push-supportedness gate (gap G8). Returns:
 *   true       — capability gate passes AND, when the SDK is loaded,
 *                OneSignal.Notifications.isPushSupported() agrees.
 *   false      — known-unsupported (no-GMS Huawei, Android-without-FCM,
 *                etc.) → caller should steer the user to the Telegram
 *                opt-in instead of showing an Enable button that leads
 *                nowhere.
 *
 * Safe to call pre-init: when the SDK hasn't loaded yet we trust the
 * capability gate (which already covers SW/PushManager/Notification +
 * installed-PWA-on-iOS). The richer SDK check kicks in after warmPushInit
 * resolves.
 */
export function isPushSupported() {
  if (!canRequestPushPermission()) return false;
  if (_initResolved && _onesignalInstance?.Notifications?.isPushSupported) {
    try {
      return _onesignalInstance.Notifications.isPushSupported() === true;
    } catch {
      return true; // Be permissive on SDK error — gate already passed.
    }
  }
  return true;
}

/**
 * Read-only push subscription state for status surfaces (e.g. the Profile
 * NotificationsStatusCard — NEW-G26). Returns:
 *   'subscribed'      — SDK loaded AND `optedIn === true`.
 *   'not-subscribed'  — SDK loaded AND `optedIn === false`.
 *   'unknown'         — SDK not loaded yet (pre-init or non-grantee). The
 *                       caller MUST treat 'unknown' as "do not assert state"
 *                       and fall back to whatever signal it has (typically
 *                       `Notification.permission === 'granted'`).
 *
 * Safe to call from any environment — bails to 'unknown' in TMA/SSR/pre-init.
 * Best-effort: a thrown getter is caught and returns 'unknown'.
 */
export function getPushSubscriptionState() {
  if (typeof window === 'undefined') return 'unknown';
  if (isTma()) return 'unknown';
  if (!_initResolved || !_onesignalInstance) return 'unknown';
  try {
    const optedIn = _onesignalInstance?.User?.PushSubscription?.optedIn;
    if (optedIn === true) return 'subscribed';
    if (optedIn === false) return 'not-subscribed';
  } catch { /* noop */ }
  return 'unknown';
}

/**
 * Pre-flight state machine for the priming soft-ask (gap G8 + research Section 1.3).
 *
 * Returns the action App.vue should take, derived from native permission
 * state, push supportedness, and the per-user priming state. The caller is
 * responsible for showing the right dialog — this function does NOT touch
 * the DOM.
 *
 * Decisions:
 *   granted             → action: 'skip'             — already subscribed.
 *   denied              → action: 'settings-helper'  — show the "enable in
 *                                                       Settings" helper
 *                                                       routed to
 *                                                       /notifications/preferences/.
 *   unsupported         → action: 'telegram-helper'  — surface the
 *                                                       Telegram-link CTA
 *                                                       at the same money-
 *                                                       moment.
 *   soft-declined SAME  → action: 'skip'             — research Section 1.4: never
 *                                                       re-ask at the same
 *                                                       moment after a "No".
 *   default             → action: 'soft-ask'         — show the moment-aware
 *                                                       priming dialog.
 */
export function pushPreflightDecision(moment, userId) {
  if (isTma()) return { action: 'skip', reason: 'tma' };
  if (typeof window === 'undefined') return { action: 'skip', reason: 'no-window' };
  if (!canRequestPushPermission()) return { action: 'skip', reason: 'capability' };

  // Permission native — read once, branch first (cheapest check).
  let perm = 'default';
  try {
    perm = window.Notification?.permission || 'default';
  } catch { /* noop */ }
  if (perm === 'granted') return { action: 'skip', reason: 'granted' };
  if (perm === 'denied') return { action: 'settings-helper', reason: 'denied' };

  // SDK supportedness — only reliable after warm. If unsupported, route
  // the user to Telegram instead of a dead Enable button.
  if (!isPushSupported()) return { action: 'telegram-helper', reason: 'unsupported' };

  // Per-user re-ask cadence — never burn the prompt twice on the same
  // moment after a soft decline.
  const state = getPrimingState(userId);
  if (state.status === 'soft-declined' && state.lastMoment === moment) {
    return { action: 'skip', reason: 'soft-declined-same-moment' };
  }
  if (state.status === 'denied') return { action: 'settings-helper', reason: 'denied-cached' };

  return { action: 'soft-ask', reason: 'default' };
}

/**
 * Run-from-Enable-click handler — gesture-bound init + consent + optIn.
 *
 * KEPT as a backward-compatible wrapper around the warm/fire split for the
 * boot-time returning-grantee path and tests that pre-date G7. New value-
 * moment screens should call `warmPushInit()` on mount and `firePushOptIn()`
 * from the click handler instead.
 *
 * Online-guard: navigator.onLine === false → bail without snoozing so the
 * primer re-fires next online session.
 */
async function enablePushFromGesture() {
  if (typeof navigator !== 'undefined' && navigator.onLine === false) return false;
  const warm = await warmPushInit();
  if (!warm) return false;
  return firePushOptIn();
}

/**
 * Public composable surface. Call from App.vue <script setup>.
 */
export function usePushNotifications() {
  const requiresGesture = ref(false);

  onScopeDispose(() => {
    // No-op placeholder for now. The OneSignal page-SDK doesn't expose a
    // dispose hook; its event listeners are owned by the global namespace
    // and shared across the app lifetime. Future per-listener teardown
    // (e.g. the Notifications click listener wired in Task 3) goes here.
  });

  return {
    initialize: async () => {
      const result = await initialize();
      requiresGesture.value = result.requiresGesture;
      return result;
    },
    // Warm/fire split (G7).
    warmPushInit,
    firePushOptIn,
    // Welcome push — fires on every new subscription (first, resubscribe, new device).
    triggerWelcomePush,
    // Backward-compat — wraps warm + fire.
    enablePushFromGesture,
    ensurePushIdentity,
    pushLogoutCleanup,
    canRequestPushPermission,
    nativePermissionGranted,
    isPushSupported,
    getPushSubscriptionState,
    pushPreflightDecision,
    // Per-user priming state (G8).
    getPrimingState,
    setPrimingState,
    clearPrimingState,
    // Legacy global snooze — retained for the boot-time fallback only.
    isPrimingSnoozed,
    snoozePriming,
    clearPrimingSnooze,
    buildInitOptions,
    navigateTo,
    requiresGesture: readonly(requiresGesture),
    /** @internal — for tests */
    _state: () => ({ _initStarted, _initResolved }),
  };
}

/**
 * @internal — reset module-scope state. ONLY for tests.
 */
export function __resetForTests() {
  _initStarted = false;
  _initResolved = false;
  _initPromise = null;
  _onesignalInstance = null;
  _clickListenerBound = false;
  _foregroundListenerBound = false;
  _changeListenerBound = false;
}
